/**
 * Sensei AI.
 *
 * Built against taskdojo-app's AIChatScreen, with the consent gate from its
 * SenseiAIScreen folded in -- the phone splits them because it needs a separate
 * settings screen; a browser does not.
 *
 * The chat itself runs on plumbing that already worked: ChatContext calls the
 * `sendAIMessage` Cloud Function, and approve/dismiss go through the same
 * context as on the phone. What was missing was a screen worth looking at, and
 * a couple of things in the payload.
 *
 * Suggested actions are the part that matters. Sensei does not act on its own:
 * it proposes -- create this task, complete that habit -- and nothing happens
 * until you approve. Both states are kept visible afterwards, approved and
 * dismissed alike, so the conversation still reads as a record of what was
 * actually done.
 *
 * The context payload is assembled here and sent with every message, which is
 * what decides how good the answers are. It now matches more of the phone's:
 * incomplete tasks rather than all of them, today's habit completions, and the
 * user's lists and notes. Moods, affirmations and budget are still absent
 * because the web has no screens for them yet -- they are omitted rather than
 * sent empty, since an empty array reads to a model as "none", not "unknown".
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useApp } from '../../contexts/AppContext';
import { useChat } from '../../contexts/ChatContext';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useGanttCharts } from '../../contexts/GanttChartsContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ScreenHeader, PrimaryButton, SecondaryButton, useInputClass, raised, pressable,
} from '../dojo';
import DojoMark from '../common/DojoMark';
import SimpleMarkdown from '../common/SimpleMarkdown';
import { formatDateTime } from '../../utils/datetime';

/** The phone's DEFAULT_CATEGORIES. */
const DEFAULT_CATEGORIES = ['General', 'Work', 'Personal', 'Health', 'Learning', 'Finance', 'Other'];

/** A human sentence for each action type the model can propose. */
const describeAction = (type: string, p: any = {}): string => {
  const when = (v: any) => (v ? formatDateTime(v) : '');
  switch (type) {
    case 'create_task': return `Create task: “${p.title}”`;
    case 'update_task': return `Update task: “${p.title || p.taskId}”`;
    case 'complete_task': return `Complete task: “${p.title || p.taskId}”`;
    case 'delete_task': return `Delete task: “${p.title || p.taskId}”`;
    case 'create_habit': return `Create habit: “${p.title}”`;
    case 'update_habit': return `Update habit: “${p.title || p.habitId}”`;
    case 'complete_habit': return `Mark habit done: “${p.title || p.habitId}”`;
    case 'delete_habit': return `Delete habit: “${p.title || p.habitId}”`;
    case 'add_mood': return `Log mood: ${p.mood || p.moodId || ''}`;
    case 'create_list': return `Create list: “${p.name}”`;
    case 'delete_list': return `Delete list: “${p.name || p.listId}”`;
    case 'add_list_item': return `Add to list: “${p.text}”`;
    case 'toggle_list_item': return `Tick list item: “${p.text || p.itemId}”`;
    case 'delete_list_item': return `Remove list item: “${p.text || p.itemId}”`;
    case 'share_list': return `Share list “${p.name || p.listId}”`;
    case 'create_affirmation': return `Add affirmation: “${p.text}”`;
    case 'delete_affirmation': return 'Delete affirmation';
    case 'create_note': return `Create note: “${p.title || 'Untitled'}”`;
    case 'update_note': return `Update note: “${p.title || p.noteId}”`;
    case 'delete_note': return `Delete note: “${p.title || p.noteId}”`;
    case 'create_gantt_chart': return `Create chart: “${p.name}”`;
    case 'add_task_to_chart': return 'Add task to chart';
    case 'remove_task_from_chart': return 'Remove task from chart';
    case 'create_category': return `Create category: “${p.name}”`;
    case 'delete_category': return `Delete category: “${p.name}”`;
    default: return type.replace(/_/g, ' ');
  }
  void when;
};

/** The extra line under an action, where the payload has anything to add. */
const actionDetail = (p: any = {}): string => {
  const bits: string[] = [];
  if (p.category) bits.push(p.category);
  if (p.status) bits.push(String(p.status).replace(/_/g, ' '));
  if (p.progress !== undefined) bits.push(`${p.progress}%`);
  if (p.deadline) bits.push(`due ${formatDateTime(p.deadline)}`);
  if (p.startDateTime) bits.push(`from ${formatDateTime(p.startDateTime)}`);
  if (p.reminder) bits.push(`reminder ${formatDateTime(p.reminder)}`);
  return bits.join(' · ');
};

const SenseiAI: React.FC = () => {
  const { state, getTodayCompletions } = useApp();
  const { connections } = useConnections();
  const { charts } = useGanttCharts();
  const { colors, isDark } = useTheme();
  const input = useInputClass();
  const {
    messages, loading, sending, aiConsented,
    sendMessage, approveAction, dismissAction, clearChat, acceptAiConsent, revokeAiConsent,
  } = useChat();

  const uid = state.user?.uid;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [lists, setLists] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);

  const endRef = useRef<HTMLDivElement>(null);

  // Lists and notes are not in AppContext, so they are read here purely to go
  // into the payload -- the phone sends both and its answers are better for it.
  useEffect(() => {
    if (!uid) return;
    const unsubL = onSnapshot(
      query(collection(db, 'lists'), where('members', 'array-contains', uid)),
      (s) => setLists(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {},
    );
    const unsubN = onSnapshot(
      query(collection(db, 'notes'), where('userId', '==', uid)),
      (s) => setNotes(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {},
    );
    return () => { unsubL(); unsubN(); };
  }, [uid]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: messages.length > 1 ? 'smooth' : 'auto' });
  }, [messages.length, sending]);

  const userData = useMemo(() => ({
    userId: uid,
    displayName: state.user?.displayName || 'User',
    // Incomplete only, as the phone sends -- a model given every finished task
    // spends its context on history rather than on what is outstanding.
    tasks: (state.tasks || []).filter((t: any) => !t.completed && t.status !== 'completed').slice(0, 20),
    habits: (state.habits || []).slice(0, 20),
    todayCompletions: getTodayCompletions(),
    lists: lists.slice(0, 15),
    notes: notes.slice(0, 15),
    connections: connections.slice(0, 20),
    ganttCharts: charts,
    defaultCategories: DEFAULT_CATEGORIES,
    customCategories: (state.user as any)?.customCategories || [],
  }), [uid, state.user, state.tasks, state.habits, getTodayCompletions, lists, notes, connections, charts]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    setText(''); setError('');
    try {
      await sendMessage(body, userData);
    } catch (err: any) {
      setError(err?.message || "Sensei couldn't answer. Please try again.");
      setText(body);
    }
  };

  const onAction = async (
    messageId: string, index: number, fn: () => Promise<unknown>, failure: string,
  ) => {
    const key = `${messageId}:${index}`;
    setBusy(key); setError('');
    try { await fn(); } catch (err: any) { setError(err?.message || failure); }
    finally { setBusy(null); }
  };

  /* ------------------------------ consent ------------------------------ */
  if (aiConsented === false) {
    return (
      <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
        <div className="max-w-2xl mx-auto pt-4 pb-24">
          <ScreenHeader title="Sensei AI" />
          <div className="px-5">
            <div className={`rounded-[18px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-6`}>
              <div className="flex items-center gap-3 mb-4">
                <DojoMark size={40} outlineColor={isDark ? 'rgba(250,247,240,0.22)' : undefined} />
                <h2 className="text-[20px] font-black tracking-[-0.4px]">Sensei is off</h2>
              </div>
              <p className={`text-[14.5px] leading-relaxed mb-3 ${colors.textSecondary}`}>
                Sensei uses Claude, an AI assistant by Anthropic. When you turn it on, your
                messages and the app data listed below are sent to Anthropic's API to generate
                a reply.
              </p>
              <ul className={`text-[14px] leading-relaxed mb-4 list-disc ml-5 space-y-1 ${colors.textSecondary}`}>
                <li>your open tasks, habits and today's completions</li>
                <li>your lists, notes and the names of your connections</li>
                <li>your display name</li>
              </ul>
              <p className={`text-[14.5px] font-semibold mb-6 ${colors.textSecondary}`}>
                Your data is not used to train AI models. You can turn Sensei off again at any time.
              </p>
              <PrimaryButton onClick={acceptAiConsent}>Turn Sensei on</PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* -------------------------------- chat -------------------------------- */
  return (
    <div className={`min-h-screen flex flex-col ${colors.bg} ${colors.text}`}>
      <div className="max-w-2xl w-full mx-auto flex-1 flex flex-col pt-4 pb-24">
        <ScreenHeader
          title="Sensei AI"
          action={
            messages.length > 0 ? (
              <button
                type="button"
                onClick={() => setConfirmClear(true)}
                className={`h-10 px-3 rounded-[12px] flex items-center gap-1.5 flex-shrink-0
                  ${raised} ${pressable} ${colors.cardBorder} ${colors.card}
                  ${colors.textSecondary} text-[13px] font-extrabold`}
              >
                Clear
              </button>
            ) : undefined
          }
        />

        {error && (
          <div role="alert" className="mx-5 mb-3 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="flex-1 px-5 space-y-4">
          {loading ? (
            <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Loading…</p>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center text-center py-16">
              <div className="mb-4">
                <DojoMark size={56} outlineColor={isDark ? 'rgba(250,247,240,0.22)' : undefined} />
              </div>
              <h2 className="text-xl font-black tracking-[-0.4px] mb-1.5">Start a conversation</h2>
              <p className={`text-[15px] font-semibold mb-5 ${colors.textSecondary}`}>
                Ask about your tasks, habits, or anything productivity-related.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  "What should I focus on today?",
                  "How are my habits going?",
                  "What's overdue?",
                ].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setText(q)}
                    className={`px-[15px] py-[9px] rounded-full border-2 text-[13.5px] font-extrabold
                      ${colors.cardBorder} ${colors.textSecondary}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => {
              const isUser = m.role === 'user';
              return (
                <div key={m.id} className={`flex gap-2.5 ${isUser ? 'justify-end' : ''}`}>
                  {!isUser && (
                    <span className="flex-shrink-0 mt-1">
                      <DojoMark size={26} outlineColor={isDark ? 'rgba(250,247,240,0.22)' : undefined} />
                    </span>
                  )}
                  <div className={isUser ? 'max-w-[80%]' : 'flex-1 max-w-[85%]'}>
                    <div
                      className={`rounded-[15px] px-4 py-3 text-[14.5px] leading-relaxed
                        ${isUser
                          ? 'bg-dojo-red text-white border-2 border-b-[3px] border-dojo-redDeep'
                          : `${colors.card} border-2 border-b-[3px] ${colors.cardBorder}`}`}
                    >
                      {isUser
                        ? <p className="whitespace-pre-wrap">{m.content}</p>
                        : <SimpleMarkdown text={m.content} />}
                    </div>

                    {/* Proposals. Nothing is done until it is approved. */}
                    {m.suggestedActions?.map((a: any, i: number) => {
                      const key = `${m.id}:${i}`;
                      const detail = actionDetail(a.payload);
                      return (
                        <div
                          key={key}
                          className={`mt-2 rounded-[14px] border-2 border-b-[3px] ${colors.cardBorder}
                            ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'} px-3.5 py-3`}
                        >
                          <p className="text-[13.5px] font-extrabold">
                            {describeAction(a.type, a.payload)}
                          </p>
                          {detail && (
                            <p className={`text-[12px] font-semibold mt-0.5 ${colors.textSecondary}`}>
                              {detail}
                            </p>
                          )}

                          {a.approved ? (
                            <p className="mt-2 text-[12.5px] font-extrabold text-dojo-greenDark">✓ Approved</p>
                          ) : a.dismissed ? (
                            <p className={`mt-2 text-[12.5px] font-extrabold ${colors.textMuted}`}>Dismissed</p>
                          ) : (
                            <div className="flex gap-2 mt-2.5">
                              <PrimaryButton
                                onClick={() => onAction(m.id, i,
                                  () => approveAction(m.id, i), "Couldn't do that.")}
                                disabled={busy === key}
                                className="!pt-2 !pb-1.5 !px-4 !text-[13px]"
                              >
                                {busy === key ? 'Working…' : 'Approve'}
                              </PrimaryButton>
                              <SecondaryButton
                                onClick={() => onAction(m.id, i,
                                  () => dismissAction(m.id, i), "Couldn't dismiss that.")}
                                disabled={busy === key}
                                className="!pt-2 !pb-1.5 !px-4 !text-[13px]"
                              >
                                Dismiss
                              </SecondaryButton>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}

          {sending && (
            <div className="flex gap-2.5 items-center">
              <DojoMark size={26} outlineColor={isDark ? 'rgba(250,247,240,0.22)' : undefined} />
              <div className={`rounded-[15px] px-4 py-3 border-2 ${colors.cardBorder} ${colors.card}
                flex gap-1.5 items-center`}>
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-slate-400' : 'bg-stone-400'}`}
                    style={{ animation: 'pulse 1.2s ease-in-out infinite', animationDelay: `${i * 0.2}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>
      </div>

      {/* Composer, pinned above the mobile bar. */}
      <div className={`fixed bottom-0 left-0 right-0 lg:left-64 z-30 border-t-2
        ${colors.cardBorder} ${colors.card} pb-20 lg:pb-0`}>
        <form onSubmit={send} className="max-w-2xl mx-auto flex gap-2 p-3">
          <input
            className={`${input} flex-1`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ask Sensei…"
            disabled={sending}
          />
          <PrimaryButton type="submit" disabled={sending || !text.trim()}>
            {sending ? '…' : 'Send'}
          </PrimaryButton>
        </form>
      </div>

      {confirmClear && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">Clear this conversation?</h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              The messages go. Anything you already approved stays done.
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirmClear(false)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton
                onClick={() => { setConfirmClear(false); clearChat().catch(() => {}); }}
                className="flex-1"
              >
                Clear
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Turning Sensei off again, as the phone allows. */}
      {aiConsented && messages.length === 0 && (
        <div className="max-w-2xl mx-auto px-5 pb-4 w-full">
          <button
            type="button"
            onClick={revokeAiConsent}
            className={`text-[12.5px] font-bold ${colors.textMuted} hover:text-dojo-red transition-colors`}
          >
            Turn Sensei off
          </button>
        </div>
      )}
    </div>
  );
};

export default SenseiAI;
